<template>
    <v-container fluid>
        <v-row dense>

            <!-- edit integration form -->
            <v-col>
                <v-alert
                    border="top"
                    color="green lighten-2"
                    dark
                    dismissible
                    close-text="Close"
                    v-model="successAlert.active"
                >
                  {{ successAlert.message }}
                </v-alert>

                <v-alert
                    border="top"
                    color="red lighten-2"
                    dark
                    dismissible
                    close-text="Close"
                    v-model="errorAlert.active"
                >
                  {{ errorAlert.message }}
                </v-alert>

                <v-card
                    color="light"
                    padding
                    elevation="6">

                    <v-card-title class="headline">
                        Edit Integration
                        <v-spacer></v-spacer>
                        <v-switch v-model="isActivate" color="primary" :disabled="isDeleted || isActivating"></v-switch><span class="subtitle-2">Activate</span>
                        <v-progress-circular v-if="isActivating" indeterminate size="16" width="2" color="primary" class="ml-2"></v-progress-circular>
                    </v-card-title>


                    <v-row>
                        <v-col md="8">
                            <div class="pl-4 mb-5" v-if="isAdmin">

                                <v-btn class="mx-1" @click="onSyncNow" x-small :disabled="isSyncNow || isLinkedToMsc" :title="isLinkedToMsc ? mscLinkedHint : ''">
                                    <span v-if="isSyncNow">
                                        <v-progress-circular
                                                indeterminate
                                                size="12"
                                                width="2"
                                                color="error darken-3">
                                        </v-progress-circular>
                                        Run Now
                                    </span>
                                    <span v-else>
                                        Run Now
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" @click="showGetRecordModal = true" x-small :disabled="isLinkedToMsc" :title="isLinkedToMsc ? mscLinkedHint : ''">
                                    <span>
                                        Get Record
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" @click="showSyncBackModal = true" x-small :disabled="isLinkedToMsc" :title="isLinkedToMsc ? mscLinkedHint : ''">
                                    <span>
                                        Sync Back
                                    </span>
                                </v-btn>

                                <!-- Check Updates Back is deliberately NOT disabled when linked. It
                                     reconciles MachShip consignments against records that ALREADY exist
                                     in FS, and pre-link records are never migrated across — same
                                     reasoning as the resync / re-update buttons. -->
                                <v-btn class="mx-1" @click="showCheckUpdatesBackModal = true" x-small>
                                    <span>
                                        Check Updates Back
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" v-if="id > 0" :href="`/logs?filter=${id}`" x-small>
                                    <span>
                                        View Logs
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" v-if="this.liverate" :href="`/live-rates/logs?filter=${id}`" x-small>
                                    <span>
                                        View Liverate
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" v-if="githubCFSPath" :href="githubCFSPath" target="_blank" x-small>
                                    <span>
                                        Custom functions
                                    </span>
                                </v-btn>
                                <v-btn class="mx-1" v-if="id > 0" :href="`/docs/integrations/integration_${id}`" target="_blank" x-small>
                                    <span>
                                        Integration Docs
                                    </span>
                                </v-btn>

                                <v-btn class="mx-1" v-if="platform != '' && id > 0" :href="`/docs/platforms/supported/${platform}`" target="_blank" x-small>
                                    <span>
                                        {{platform}} Docs
                                    </span>
                                </v-btn>

                                <v-btn x-small class="mx-1" color="primary">
                                    <v-menu v-model="menu" offset-y>
                                        <template v-slot:activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on" style="cursor: pointer; color: white;">
                                                Actions
                                                <v-icon>{{ menu ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                            </span>
                                        </template>

                                        <v-list>
                                            <v-list-item>
                                                <v-list-item-title><ExportModal v-if="isAdmin" :id="id" /></v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title><ImportRecordModal v-if="isAdmin" @recordImported="onRecordImported" /></v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title><ConfirmArchiveModal v-if="isAdmin" :integration="integration" :id="id" /></v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-btn>

                            </div>
                            <div class="pl-5" v-if="integration && integration.client_token">
                                <v-btn icon class="mr-3" @click="onCopyToken">
                                    <v-icon>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col md="4">
                            <div class="pr-4 d-flex align-items-center justify-content-end">
                                <v-switch v-model="liverate" @change="onLiveRateChange" color="primary" :disabled="isDeleted" ></v-switch>
                                <span class="subtitle-2">Live rates</span>
                            </div>

                            <IntegrationsLastSync
                                v-if="integration && integration.last_syncs"
                                class="pr-4"
                                :last_syncs="integration.last_syncs"
                            />
                        </v-col>

                    </v-row>

                    <v-card-title>
                        <v-spacer></v-spacer>
                        <v-switch @change="checkBilling" v-model="isTestMode" color="primary" :disabled="isDeleted"></v-switch><span class="subtitle-2">Test Mode</span>
                    </v-card-title>

                    <get-record-modal
                        :integration="id"
                        v-if="showGetRecordModal"
                        :targetFields="getTargetFields"
                        @getRecordModalClose="showGetRecordModal = false">
                    </get-record-modal>
                    <sync-back-modal
                        :integration="id"
                        v-if="showSyncBackModal"
                        @syncBackModalClose="showSyncBackModal = false">
                    </sync-back-modal>
                    <check-updates-back-modal
                        :integration="id"
                        v-if="showCheckUpdatesBackModal"
                        @modalClose="showCheckUpdatesBackModal = false">

                    </check-updates-back-modal>

                    <div ref="firstTab"></div>

                    <integrations-form
                        ref="integrationsForm"
                        v-if="integration"
                        :integration="integration"
                        :integration_id="id"
                        :integration_type_id="integration.integration_type_id"
                        :submit="onUpdate"
                        :isSubmit="isSubmit"
                        :is_disabled="isDeleted"
                        submitText="Update">
                    </integrations-form>
                    <v-progress-linear
                        v-else
                        indeterminate
                        color="primary"
                        class="mb-5"
                    ></v-progress-linear>


                    <div class="mt-5 p-3" v-if="integration && !isDeleted">
                        <integrations-mapping :integration="integration"></integrations-mapping>
                    </div>

                </v-card>
            </v-col>
        </v-row>

        <Modal
            ref="confirmation"
            color="warning"
            okText="update" />

        <Modal
            ref="billingPrompt"
            color="warning"
            width="450"
            :persistent="false"
            cancelText="Close"
            okText="Open Billing Settings" />
        <v-overlay
            :value="isActivating"
            :z-index="5000">
            <v-progress-circular
                indeterminate
                size="128">
                {{ overlayMessage }}
            </v-progress-circular>
        </v-overlay>
    </v-container>
</template>

<script>
import IntegrationsForm from './forms/IntegrationsForm.vue';
import IntegrationsMapping from './IntegrationsMapping.vue';
import IntegrationsLastSync from "./IntegrationsLastSync";
import GetRecordModal from "./modal/GetRecordModal";
import SyncBackModal from "./modal/SyncBackModal";
import CheckUpdatesBackModal from "./modal/CheckUpdatesBackModal";
import ExportModal from "./modal/ExportModal";
import Modal from "@/js/components/Modal.vue";
import { EventBus } from '@/js/event-bus.js';
import ImportRecordModal from "~/integrations/modal/ImportRecordModal";
import ConfirmArchiveModal from "~/integrations/modal/ConfirmArchiveModal";
const localPrefix = window.IS_LOCAL ? "[LOCAL] ":"";

export default {
    components: {
        IntegrationsForm,
        IntegrationsMapping,
        IntegrationsLastSync,
        GetRecordModal,
        SyncBackModal,
        CheckUpdatesBackModal,
        ExportModal,
        Modal,
        ImportRecordModal,
        ConfirmArchiveModal
    },

    data() {
        return {
            id: null,
            integration: null,
            isSubmit: false,
            tab: 'tab-machship-keys',
            isActivate: null,
            isTestMode: null,
            isSyncNow:false,
            isActivating: false,
            _activationStartAt: null,
            overlayMessage: 'Updating status...',
            liverate: false,
            showGetRecordModal: false,
            showSyncBackModal: false,
            showCheckUpdatesBackModal: false,
            successAlert: {
                active: false,
                message: ''
            },
            errorAlert: {
                active: false,
                message: ''
            },
            isDeleted: false,
            platform: '',
            menu: false,

            // for tracking billing changes
            billing: {}
        }
    },

    computed: {
        isAdmin() {
            return this.$store.getters.isAdmin
        },

        getLiverate() {
            return this.integration.metas.find(meta => (meta.meta_key == 'LIVE_RATE'))
        },

        /**
         * MSC3-1179. Answered by the backend (IntegrationDetailsTransformer), not
         * derived from the metas here: being linked means MSC_LINK_INTEGRATION is
         * set AND MSC_LINK_ENV resolves to a known MSC. A half-configured
         * integration still runs normally in FS, so keying off the UUID alone
         * would disable these controls while FS was still doing the work.
         *
         * TEMPORARY: goes when linking goes. See docs/tasks/msc-link-poc.md.
         */
        isLinkedToMsc() {
            return this.integration.msc_linked === true
        },

        mscLinkedHint() {
            return 'This integration is linked to MachShip Connect. '
                + 'Records are created there, so run this from MSC instead.'
        },

        githubCFSPath() {
            if (this.id) {
                return 'https://github.com/machship/fusedship-sync/tree/master/app/Services/CustomFunctions/integration_' + this.id;
            }
        },

        getTargetFields() {
            return this.integration.target_fields ?? [];
        }
    },

    mounted() {
        this.id = parseInt(this.$route.params.id)

        // Handle flash messages from query parameters
        const query = this.$route.query;

        if (query.flash_success) {
            this.successAlert.message = query.flash_success;
            this.successAlert.active = true;
            // Remove query param from URL without reloading
            this.$router.replace({ query: { ...query, flash_success: undefined } });
        }

        if (query.flash_error) {
            this.errorAlert.message = query.flash_error;
            this.errorAlert.active = true;
            // Remove query param from URL without reloading
            this.$router.replace({ query: { ...query, flash_error: undefined } });
        }

        this.$http
            .get('/api/integrations/' + this.id)
            .then(({ data }) => {

                this.integration = data.data.attributes
                this.updateBilling(this.integration)

                this.isActivate = this.integration.integration_status == 'active' ? true : false
                this.isTestMode = this.integration.is_test == 'active' ? true : false
                this.liverate = this.integration.live_rate_active
                this.isDeleted = this.integration.deleted_at ? true : false
                // so its easier to find specific integration with multiple tabs
                document.title = this.integration.label  + ` Edit - ${localPrefix} MachShip Integrations`;


                // get the integration type of this integration
                const integType = this.integration.integration_type.display_name ?? null;
                if (integType) {
                    this.platform = integType.replace(/\s+/g, '-').toLowerCase();
                }
            })
            .catch(err => {
                let message = (typeof err.response.data.error.message !== 'undefined') ? err.response.data.error.message : err;
                EventBus.$emit('show-error', message);
                setTimeout(() => {
                    this.$router.push({name: 'integrations list' });
                }, 3000);
            })
    },

    watch: {
        isActivate: function (value, oldValue) {
            if (oldValue != null) {
                this.onSetActivation(value, oldValue)
            }
        },
        isTestMode: function (value, oldValue) {
            if (oldValue != null) {
                this.onSetMode(value)
            }
        }

    },

    methods: {

        onUpdate(data) {
            if (this.isSubmit) {
                return
            }

            this.isSubmit = true
            this.$http
                .put('/api/integrations/' + this.id, data)
                .then(res => {
                    this.updateBilling(res.data.data.attributes);
                    // show info
                    EventBus.$emit('show-success', 'Successfully saved integrations.');
                    this.isSubmit = false
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                    this.isSubmit = false
                })
        },

        onSetActivation(value, oldValue) {
            this.overlayMessage = value ? 'Activating' : 'Deactivating'
            this._activationStartAt = Date.now()
            this.isActivating = true
            let status = value ? 'active' : 'inactive'
            return this.$http
                .post('/api/integrations/activate', {
                    integration_id: this.id,
                    integration_status: status
                })
                .then(() => {
                    EventBus.$emit('show-success', 'Successfully sets integration status to ' + status)
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                    this.isActivate = oldValue
                })
                .finally(() => {
                    const minMs = 600;
                    const elapsed = this._activationStartAt ? (Date.now() - this._activationStartAt) : 0;
                    const wait = elapsed < minMs ? (minMs - elapsed) : 0;
                    setTimeout(() => {
                        this.isActivating = false;
                        this.$nextTick(() => {
                            this.checkBilling();
                        });
                    }, wait);
                })
        },

        onSetMode(value) {
            let status = value ? 'active' : 'inactive'
            this.$http
                .post('/api/integrations/mode', {
                    integration_id: this.id,
                    is_test: status
                })
                .then(() => {
                    EventBus.$emit('show-success', 'Test Mode status ' + status)
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                })
        },

        onSyncNow() {
            this.isSyncNow = true
            this.$http
                .post('/api/sync/now', {
                    integration_id: this.id
                })
                .then(({ data }) => {
                    EventBus.$emit('show-success', 'Sync Successfully Executed!!')
                    this.isSyncNow = false
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                    this.isSyncNow = false
                })
        },

        onShowLastSync() {
            this.$refs.lastSyncModal.onShow();
        },

        onLiveRateChange(value) {

            this.checkBilling()

            let toActivate = value == 1 ? 'Activate' : 'Deactivate'

            this.$refs.confirmation.onShow(
                'Confirmation',
                'Are you you want to ' + toActivate + ' Live rates?',
                async () => {
                    try {

                        let { data } = await this.$http.post('/api/integrations/activate/liverate', {
                            id: this.id,
                            activate: value
                        })

                        if (data.activation?.message) {
                            EventBus.$emit('show-error', data.activation.message)
                            this.liverate = !value

                        } else {
                            if (toActivate == 'Deactivate') {
                                EventBus.$emit('show-success', 'Successfully deactivated liverates configurations.')
                            } else {
                                EventBus.$emit('show-success', 'Successfully activated liverates configurations.')
                            }

                            setTimeout(() => {
                                window.location.reload()
                            }, 800)
                        }
                    } catch (err) {
                        EventBus.$emit('show-error', err)
                    }
                },
                () => {
                    this.liverate = !value
                }
            )
        },

        onExport() {
            // show confirmation here

        },

        onCopyToken() {
            if (this.integration.client_token) {
                navigator.clipboard.writeText(this.integration.client_token)
                EventBus.$emit('show-success', "Copy client token to clipboard");
            }
        },

        onRecordImported(newRecord) {
            this.record = newRecord;
        },

        checkBilling() {
            const billing = this.billing;

            const isSetupChargedOn = billing.setup_charged;
            const hasSetupFee = billing.setup_fee > 0;
            const invalidSetupFee = isSetupChargedOn && !hasSetupFee;

            const isMonthlyChargedOn = billing.monthly_charged;
            const hasMonthlyFee = billing.monthly_fee > 0;
            const invalidMonthlyFee = isMonthlyChargedOn && !hasMonthlyFee;

            // if setup fee is switched on but no setup fee is set
            // or monthly fee is switched on but no monthly fee is set
            if (invalidSetupFee || invalidMonthlyFee) {
                this.$refs.billingPrompt.onShow(
                    'Ensure billing information is updated',
                    `If making an integration live, please confirm the billing dates and amounts are updated.`,
                    () => {
                        this.$refs.integrationsForm.switchToBillingTab()
                        this.$nextTick(() => {
                            this.$refs.firstTab.scrollIntoView({ behavior: "smooth" });
                        });
                    }
                )
            }
        },

        updateBilling(integration) {
            this.billing = {
                setup_charged: integration.setup_charged,
                setup_fee: integration.setup_fee,
                monthly_charged: integration.monthly_charged,
                monthly_fee: integration.monthly_fee,
            }
        }
    }
}
</script>
